/**
 * timedRuns.js — Phase 6 evidence store for ELIGIBLE TIMED RUNS.
 *
 * A single, per-active-learner ledger of timed activity results that
 * feeds Explorer Rank (Phase 6). This is the ONE canonical source — it
 * does not create a parallel mastery/progress system; it records the
 * raw timed-run evidence that the rank engine reads.
 *
 * Scoping: keyed by the active learner id (getActiveChildId). The child
 * experience is account-free, so when no explicit learner is selected we
 * fall back to a single local anon bucket ("local"). We NEVER fall back
 * to the first/last/seeded child.
 */
import { getActiveChildId } from "@/lib/children";

const KEY = "mwq_timed_runs_v1";
const FOCUS_KEY = "mwq_focus_subject_v1";
const MAX_PER_SUBJECT = 50; // keep a rolling window; rank uses last 10

export const RANK_SUBJECTS = ["maths", "english", "geography", "science"];

function learnerId() {
  try {
    return getActiveChildId() || "local";
  } catch {
    return "local";
  }
}

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function writeAll(obj) {
  try {
    localStorage.setItem(KEY, JSON.stringify(obj));
  } catch {
    /* ignore quota */
  }
}

/**
 * Record one completed eligible timed run.
 * @param {object} run
 * @param {string} run.subject
 * @param {number} run.accuracy      0..1 fraction correct
 * @param {number} run.avgResponseMs average ms per question
 * @param {number} run.completionMs  total run duration ms
 * @param {number} run.questionCount
 * @param {string} [run.difficulty]
 * @param {string[]} [run.skills]
 */
export function logTimedRun(run) {
  if (!run || !run.subject) return null;
  const all = readAll();
  const lid = learnerId();
  all[lid] = all[lid] || {};
  const subj = run.subject;
  const list = Array.isArray(all[lid][subj]) ? all[lid][subj] : [];
  const entry = {
    learnerId: lid,
    subject: subj,
    accuracy: Math.max(0, Math.min(1, Number(run.accuracy) || 0)),
    avgResponseMs: Math.max(0, Number(run.avgResponseMs) || 0),
    completionMs: Math.max(0, Number(run.completionMs) || 0),
    questionCount: Math.max(0, Number(run.questionCount) || 0),
    difficulty: run.difficulty || "standard",
    skills: Array.isArray(run.skills) ? run.skills : [],
    timestamp: new Date().toISOString(),
  };
  list.push(entry);
  // Keep only the most recent MAX_PER_SUBJECT.
  all[lid][subj] = list.slice(-MAX_PER_SUBJECT);
  writeAll(all);
  try {
    window.dispatchEvent(new CustomEvent("mwq-timed-run-logged", { detail: { subject: subj } }));
  } catch {
    /* ignore */
  }
  return entry;
}

/** All runs for a subject (oldest → newest) for the active learner. */
export function getRuns(subject) {
  const all = readAll();
  const lid = learnerId();
  const list = all?.[lid]?.[subject];
  return Array.isArray(list) ? list : [];
}

/** Last N runs (newest → oldest). */
export function getRecentRuns(subject, n = 10) {
  return getRuns(subject).slice(-n).reverse();
}

/** Subjects that have at least one recorded run for the active learner. */
export function subjectsWithRuns() {
  const all = readAll();
  const lid = learnerId();
  return Object.keys(all?.[lid] || {}).filter((s) => (all[lid][s] || []).length > 0);
}

// ── Focus subject (which subject the homepage headline rank shows) ──
export function getFocusSubject() {
  try {
    const stored = localStorage.getItem(FOCUS_KEY);
    if (stored && RANK_SUBJECTS.includes(stored)) return stored;
  } catch {
    /* ignore */
  }
  // Prefer a subject the learner has actually practised.
  const practised = subjectsWithRuns();
  if (practised.length) return practised[practised.length - 1];
  return "maths";
}

export function setFocusSubject(subject) {
  if (!RANK_SUBJECTS.includes(subject)) return;
  try {
    localStorage.setItem(FOCUS_KEY, subject);
    window.dispatchEvent(new CustomEvent("mwq-focus-subject-changed", { detail: { subject } }));
  } catch {
    /* ignore */
  }
}
